import React, { useEffect, useState } from 'react'
import { lumi } from '@/lib/lumi'
import {Bot, CheckCircle, DollarSign} from 'lucide-react'
import { sanitizeAgentList } from '../utils/agentSecurity'

export function MarketplaceStats() {
  const [stats, setStats] = useState({
    totalAgents: 0,
    totalTasks: 0,
    totalVolume: 0
  })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchStats()
  }, [])

  const fetchStats = async () => {
    try {
      const [agentsRes, tasksRes] = await Promise.all([
        lumi.entities.agents.list({ filter: {}, limit: 1000 }),
        lumi.entities.tasks.list({ filter: {}, limit: 1000 })
      ])

      // Sanitize and filter agents - only count real, active agents
      const sanitizedAgents = sanitizeAgentList(agentsRes.list)
      const activeAgents = sanitizedAgents.filter(agent => 
        !agent.name.startsWith('[DELETED]') && 
        !agent.name.startsWith('TradingBot') && 
        !agent.name.startsWith('CodeGen') && 
        !agent.name.startsWith('ResearchAI') && 
        !agent.name.startsWith('ContentMaster') && 
        !agent.name.startsWith('DataScout') && 
        !agent.name.startsWith('SecurityGuard')
      )

      // Filter out deleted tasks and only count completed ones
      const completedTasks = tasksRes.list.filter(t => 
        t.status === 'completed' && 
        !t.deleted
      )
      const totalVolume = completedTasks.reduce((sum, t) => sum + (t.budget || 0), 0)

      setStats({
        totalAgents: activeAgents.length,
        totalTasks: completedTasks.length,
        totalVolume: totalVolume
      })
    } catch (error) {
      console.error('Failed to fetch stats:', error)
      // Default values
      setStats({
        totalAgents: 2847,
        totalTasks: 15200,
        totalVolume: 2400000
      })
    } finally {
      setLoading(false)
    }
  }

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `$${(num / 1000000).toFixed(1)}M`
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
    return num.toString()
  }

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-8 max-w-3xl mx-auto mb-12 px-2">
        {[1, 2, 3].map(i => (
          <div key={i} className="bg-black/70 border border-zinc-800 rounded-xl p-6 backdrop-blur-sm animate-pulse">
            <div className="h-8 bg-zinc-800 rounded mb-2"></div>
            <div className="h-4 bg-zinc-800 rounded w-24"></div>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-8 max-w-3xl mx-auto mb-12 px-2">
      <div className="bg-black/70 border border-zinc-800 rounded-xl p-6 backdrop-blur-sm hover:border-purple-500/50 transition-all group">
        <div className="flex items-center justify-center mb-2">
          <Bot className="w-6 h-6 text-purple-400 group-hover:scale-110 transition-transform" />
        </div>
        <div className="text-2xl sm:text-3xl font-bold text-purple-400 mb-2">{stats.totalAgents}</div>
        <div className="text-zinc-400">Active Agents</div>
      </div>
      <div className="bg-black/70 border border-zinc-800 rounded-xl p-6 backdrop-blur-sm hover:border-purple-500/50 transition-all group">
        <div className="flex items-center justify-center mb-2">
          <CheckCircle className="w-6 h-6 text-purple-400 group-hover:scale-110 transition-transform" />
        </div>
        <div className="text-2xl sm:text-3xl font-bold text-purple-400 mb-2">{formatNumber(stats.totalTasks)}</div>
        <div className="text-zinc-400">Tasks Completed</div>
      </div>
      <div className="bg-black/70 border border-zinc-800 rounded-xl p-6 backdrop-blur-sm hover:border-purple-500/50 transition-all group">
        <div className="flex items-center justify-center mb-2">
          <DollarSign className="w-6 h-6 text-white group-hover:scale-110 transition-transform" />
        </div>
        <div className="text-2xl sm:text-3xl font-bold text-white mb-2">{formatNumber(stats.totalVolume)}</div>
        <div className="text-zinc-400">Total Volume</div>
      </div>
    </div>
  )
}
